import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { DepreciationRun, JournalEntry, LocaleCode } from '@/api/models';
import {
    useCommitDepreciationRunMutation,
    useFixedAssetsQuery,
    useInvalidatePostedAssetEntry,
    usePreviewDepreciationRunMutation,
} from '@/api/queries';
import { todayIso } from '@/pages/accounting/statements/statementMeta';
import { PATH } from '@/router/PATH';
import { entryWindowForPeriod } from '../../../registersMeta';
import {
    fixedAssetErrorMessage,
    periodFromDate,
    toRunPreviewRows,
    type RunPreviewRow,
} from '../../fixedAssetsMeta';

type UseDepreciationRunParams = {
    locale: LocaleCode;
    onClose: () => void;
};

export type DepreciationRunModel = {
    /** The date whose month is being run (the picker speaks dates). */
    periodDate: string;
    period: string;
    onPeriodDateChange: (value: string) => void;
    isPreviewing: boolean;
    isCommitting: boolean;
    previewError: string | null;
    /** The computed run; null before the first preview for this period. */
    rows: RunPreviewRow[];
    totalTetri: number;
    hasPreview: boolean;
    /** True when the preview produced no charge at all. */
    isNilRun: boolean;
    /** Non-null when this period already has a run — no second is created. */
    existingRun: DepreciationRun | null;
    onPreview: () => Promise<void>;
    onOpenExistingEntry: () => void;
    /** Persist the run's draft and open it in the Journal Entry form. */
    onReview: () => Promise<void>;
    draft: JournalEntry | null;
    onDraftClose: () => void;
    onDraftSaved: (entry: JournalEntry) => void;
};

/**
 * The depreciation run.
 *
 * Two steps, and the second is where this differs from the mock era:
 *
 * 1. **Preview** asks for the per-asset charges of a month. It writes nothing.
 *    Assets contributing zero come back with a stated reason rather than being
 *    dropped, and a `CATCH_UP` line is not a zero at all — it is several months
 *    charged at once, which is correct and is labelled with its count.
 * 2. **Review** *commits*: the server persists one balanced DRAFT entry and
 *    returns it, and that entry opens in the Journal Entry form. There is no
 *    third step — the server owns the entry, so there is nothing for the client
 *    to confirm afterwards.
 *
 * **Committing posts nothing.** The draft sits in the journal until a person
 * posts it there, under the journal's own validation and period gating; a closed
 * period drafts normally and is refused at the post, with no special case here.
 * Until it posts, no asset's accumulated depreciation moves.
 *
 * A second run for a period is impossible rather than merely discouraged: the
 * server guards it with a unique index, so a repeat commit returns the *existing*
 * draft. `existingRun` surfaces that state up front, before the user asks.
 */
export const useDepreciationRun = ({
    locale,
    onClose,
}: UseDepreciationRunParams): DepreciationRunModel => {
    const { t } = useTranslation('pages');
    const navigate = useNavigate();
    const preview = usePreviewDepreciationRunMutation();
    const commit = useCommitDepreciationRunMutation();
    const invalidatePosted = useInvalidatePostedAssetEntry();

    /**
     * The **whole** register, for the catch-up instalment counts.
     *
     * Deliberately not the page's filtered list. A run covers every eligible
     * asset whatever the screen is filtered to, so deriving the counts from a
     * Disposed filter or an active search would lose the metadata for most
     * lines and silently degrade "catching up 3 months" to the uncounted
     * fallback — a correct run made to look unexplained by an unrelated filter.
     *
     * This dialog is only mounted while it is open, so the extra read happens
     * when the user asks for a run, not on every register load.
     */
    const allAssets = useFixedAssetsQuery({ status: 'ALL', search: '' });
    const assetsById = React.useMemo(
        () =>
            new Map(
                (allAssets.data?.success
                    ? allAssets.data.response.items
                    : []
                ).map((asset) => [asset.id, asset]),
            ),
        [allAssets.data],
    );

    const [periodDate, setPeriodDate] = React.useState(todayIso);
    const [run, setRun] = React.useState<DepreciationRun | null>(null);
    const [hasDraftToCommit, setHasDraftToCommit] = React.useState(false);
    const [existingRun, setExistingRun] =
        React.useState<DepreciationRun | null>(null);
    const [previewError, setPreviewError] = React.useState<string | null>(null);
    const [draft, setDraft] = React.useState<JournalEntry | null>(null);

    const period = periodFromDate(periodDate);

    // Changing the month invalidates whatever was on screen — a preview belongs
    // to exactly one period and must never be read against another.
    React.useEffect(() => {
        setRun(null);
        setHasDraftToCommit(false);
        setExistingRun(null);
        setPreviewError(null);
    }, [period]);

    const onPreview = React.useCallback(async () => {
        setPreviewError(null);
        const result = await preview.mutateAsync({
            body: { period, locale },
            assetsById,
        });
        if (!result.success) {
            setPreviewError(fixedAssetErrorMessage(result.error, t));
            return;
        }
        setRun(result.response.run);
        setHasDraftToCommit(result.response.draft !== null);
        setExistingRun(result.response.existingRun);
    }, [preview, period, locale, assetsById, t]);

    // The Journal **tab**, scoped to the month being charged — a depreciation
    // entry is dated at that period's end, so the month always contains it, and
    // the Books shell's own window rarely does.
    const onOpenExistingEntry = React.useCallback(() => {
        const entryId = existingRun?.journalEntryId;
        const window = entryWindowForPeriod(period);
        if (entryId && window) {
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ENTRY(entryId, window),
            );
        }
    }, [existingRun, navigate, period]);

    /**
     * Persist the run and open its draft.
     *
     * The preview's draft cannot be opened directly — it is a `DraftEntryPreview`
     * with no id, because nothing behind it exists yet. The commit is what turns
     * it into a real entry, and its response is the only place a saved draft
     * comes from.
     */
    const onReview = React.useCallback(async () => {
        setPreviewError(null);
        const result = await commit.mutateAsync({
            body: { period, locale },
            assetsById,
        });
        if (!result.success) {
            setPreviewError(fixedAssetErrorMessage(result.error, t));
            return;
        }
        // `alreadyExisted` is not an error: the server handed back the draft this
        // period already had, which is exactly the no-duplicates guarantee.
        setExistingRun(result.response.run);
        setDraft(result.response.draft);
    }, [commit, period, locale, assetsById, t]);

    const onDraftClose = React.useCallback(() => {
        // The draft stays — it is persisted, and closing the form is not a
        // withdrawal. `existingRun` now holds it, so reopening this dialog for
        // the same period offers that entry rather than building a second.
        setDraft(null);
    }, []);

    const onDraftSaved = React.useCallback(
        (entry: JournalEntry) => {
            // There is no commit to make — the server links a posted entry to
            // its assets itself, which is why the three `commit…` endpoints are
            // gone. But the *register* has to be told: posting is what moves
            // every asset's accumulated depreciation and the ledger side of the
            // tie-out, and the journal's own post mutation invalidates only the
            // journal. Without this the totals and the reconciliation keep
            // showing their pre-post state.
            invalidatePosted(entry);
            setDraft(null);
            onClose();
        },
        [invalidatePosted, onClose],
    );

    const rows = React.useMemo(
        () => (run ? toRunPreviewRows(run.lines, locale) : []),
        [run, locale],
    );

    return {
        periodDate,
        period,
        onPeriodDateChange: setPeriodDate,
        isPreviewing: preview.isPending,
        isCommitting: commit.isPending,
        previewError,
        rows,
        totalTetri: run?.totalTetri ?? 0,
        hasPreview: run !== null,
        isNilRun: run !== null && !hasDraftToCommit,
        existingRun,
        onPreview,
        onOpenExistingEntry,
        onReview,
        draft,
        onDraftClose,
        onDraftSaved,
    };
};
